import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    ProgressBar,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import {
    CheckmarkCircle20Filled,
    Circle20Filled,
    Circle20Regular,
    Warning20Filled,
} from '@fluentui/react-icons';
import { UseMigrationResult } from '../hooks/useMigration';
import { MigrationStepType } from '../handlers/MigrateChatSessionHandler';
import { customColors, customFonts } from '../../../styles';

const STEPS: { key: MigrationStepType; label: string; sublabel: string }[] = [
    {
        key: 'summarizing',
        label: 'Analyzing your conversation',
        sublabel: 'Reading and summarizing your conversation history',
    },
    {
        key: 'creating',
        label: 'Setting up your new chat',
        sublabel: 'Creating an active conversation for you',
    },
    {
        key: 'seeding',
        label: 'Transferring conversation history',
        sublabel: 'Bringing your context into the new active chat',
    },
];

const stepIndex = (step: MigrationStepType | null): number => {
    if (step === 'summarizing') return 0;
    if (step === 'creating') return 1;
    if (step === 'seeding') return 2;
    if (step === 'complete') return 3;
    return -1;
};

const formatElapsed = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const useStyles = makeStyles({
    surface: {
        fontFamily: customFonts.Lato,
        borderTop: `4px solid ${customColors.protivitiOrange}`,
        borderRadius: '8px',
        ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
        minWidth: '420px',
    },
    title: {
        fontFamily: customFonts.Lato,
        color: customColors.protivitiDarkBlue,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase500,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
        fontFamily: customFonts.Lato,
        paddingTop: tokens.spacingVerticalS,
    },
    bodyText: {
        fontFamily: customFonts.Lato,
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase400,
    },
    // Idle state — "What to expect" list
    expectSection: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    expectLabel: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    expectItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
    },
    expectBullet: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: customColors.protivitiDarkBlue,
        flexShrink: 0,
        marginTop: '7px',
    },
    expectItemText: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase400,
    },
    expectItemNote: {
        fontFamily: customFonts.Lato,
        fontStyle: 'italic',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        lineHeight: tokens.lineHeightBase300,
        marginTop: '2px',
    },
    // Step list
    stepList: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    stepRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
    },
    stepIcon: {
        flexShrink: 0,
        marginTop: '1px',
    },
    stepIconDone: {
        color: customColors.green,
    },
    stepIconActive: {
        color: customColors.protivitiDarkBlue,
    },
    stepIconPending: {
        color: tokens.colorNeutralForeground4,
    },
    stepText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
    },
    stepLabel: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    stepLabelPending: {
        color: tokens.colorNeutralForeground4,
        fontWeight: tokens.fontWeightRegular,
    },
    stepSublabel: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    progressBar: {
        '& .fui-ProgressBar__bar': {
            backgroundColor: customColors.protivitiDarkBlue,
        },
        '& .fui-ProgressBar__track': {
            backgroundColor: customColors.lightGray,
        },
    },
    // Footer row: timer + warning
    footerRow: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    timer: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    warning: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalXS,
        backgroundColor: 'rgba(246, 135, 31, 0.07)',
        ...shorthands.border('1px', 'solid', 'rgba(246, 135, 31, 0.3)'),
        ...shorthands.borderRadius('4px'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
    warningIcon: {
        color: customColors.protivitiOrange,
        flexShrink: 0,
        marginTop: '1px',
    },
    warningText: {
        fontFamily: customFonts.Lato,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        lineHeight: tokens.lineHeightBase300,
    },
    error: {
        fontFamily: customFonts.Lato,
        color: tokens.colorStatusDangerForeground1,
        fontSize: tokens.fontSizeBase200,
    },
    // Buttons
    primaryButton: {
        fontFamily: customFonts.Lato,
        fontWeight: tokens.fontWeightSemibold,
        backgroundColor: customColors.protivitiDarkBlue,
        color: customColors.white,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: customColors.protivitiOrange,
            color: customColors.white,
            opacity: '0.85',
        },
        ':active': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            transition: 'all 0s',
        },
    },
    secondaryButton: {
        fontFamily: customFonts.Lato,
        backgroundColor: 'transparent',
        color: customColors.protivitiDarkBlue,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
        },
        ':active': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
            color: customColors.white,
            transition: 'all 0s',
        },
    },
    cancelActiveButton: {
        fontFamily: customFonts.Lato,
        backgroundColor: 'transparent',
        color: tokens.colorStatusDangerForeground1,
        ...shorthands.border('1px', 'solid', tokens.colorStatusDangerForeground1),
        ...shorthands.borderRadius('5px'),
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: tokens.colorStatusDangerForeground1,
            color: customColors.white,
        },
        ':active': {
            opacity: '0.8',
            transition: 'all 0s',
        },
    },
});

interface MigrationModalProps {
    open: boolean;
    sessionId: string;
    migration: UseMigrationResult;
    onDismiss: () => void;
}

const MigrationModal: React.FC<MigrationModalProps> = ({ open, sessionId, migration, onDismiss }) => {
    const styles = useStyles();
    const { state } = migration;
    const isActive = state.step !== null && state.step !== 'complete';
    const isComplete = state.step === 'complete';
    const isIdle = state.step === null && state.error === null;
    const currentStepIndex = stepIndex(state.step);

    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        if (!isActive) {
            setElapsedSeconds(0);
            return;
        }
        const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, [isActive]);

    const handleStart = (): void => {
        void migration.migrate(sessionId);
    };

    const handleDismiss = (): void => {
        migration.reset();
        onDismiss();
    };

    return (
        <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open && !isActive) handleDismiss(); }}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>
                        <span className={styles.title}>
                            {isComplete ? 'Migration Complete' : 'Migrate to Active Chats'}
                        </span>
                    </DialogTitle>
                    <DialogContent>
                        <div className={styles.body}>
                            {isIdle && (
                                <>
                                    <Text className={styles.bodyText}>
                                        Your agent conversation will be migrated to active chats, allowing you to continue seamlessly from where you left off.
                                    </Text>
                                    <div className={styles.expectSection}>
                                        <Text className={styles.expectLabel}>What to expect</Text>
                                        <div className={styles.expectItem}>
                                            <span className={styles.expectBullet} />
                                            <Text className={styles.expectItemText}>
                                                The context of your conversation will carry over into the new chat.
                                            </Text>
                                        </div>
                                        <div className={styles.expectItem}>
                                            <span className={styles.expectBullet} />
                                            <div>
                                                <Text className={styles.expectItemText}>
                                                    Files you uploaded to the chat in the past will not be transferred. You can re-upload them if needed.
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isActive && (
                                <>
                                    <div className={styles.stepList}>
                                        {STEPS.map((step, i) => {
                                            const done = i < currentStepIndex;
                                            const active = i === currentStepIndex;
                                            return (
                                                <div key={step.key} className={styles.stepRow}>
                                                    <span className={styles.stepIcon}>
                                                        {done ? (
                                                            <CheckmarkCircle20Filled className={styles.stepIconDone} />
                                                        ) : active ? (
                                                            <Circle20Filled className={styles.stepIconActive} />
                                                        ) : (
                                                            <Circle20Regular className={styles.stepIconPending} />
                                                        )}
                                                    </span>
                                                    <div className={styles.stepText}>
                                                        <Text
                                                            className={mergeClasses(styles.stepLabel, !active && !done && styles.stepLabelPending)}
                                                        >
                                                            {step.label}
                                                        </Text>
                                                        {active && (
                                                            <Text className={styles.stepSublabel}>{step.sublabel}</Text>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <ProgressBar className={styles.progressBar} />

                                    <div className={styles.footerRow}>
                                        <Text className={styles.timer}>
                                            Time elapsed: {formatElapsed(elapsedSeconds)}
                                        </Text>
                                        <div className={styles.warning}>
                                            <Warning20Filled className={styles.warningIcon} />
                                            <Text className={styles.warningText}>
                                                Longer conversations may take a few minutes. Please keep this tab open until the migration finishes.
                                            </Text>
                                        </div>
                                    </div>
                                </>
                            )}

                            {isComplete && (
                                <Text className={styles.bodyText}>
                                    Your conversation has been migrated successfully. The new Nexus chat is now open and ready to use.
                                </Text>
                            )}

                            {state.error && (
                                <Text className={styles.error}>{state.error}</Text>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        {isIdle && (
                            <>
                                <Button className={styles.secondaryButton} appearance="outline" onClick={handleDismiss}>
                                    Cancel
                                </Button>
                                <Button className={styles.primaryButton} appearance="primary" onClick={handleStart}>
                                    Start Migration
                                </Button>
                            </>
                        )}
                        {isActive && (
                            <Button
                                className={styles.cancelActiveButton}
                                appearance="outline"
                                onClick={() => { migration.cancel(); handleDismiss(); }}
                            >
                                Cancel Migration
                            </Button>
                        )}
                        {isComplete && (
                            <Button className={styles.primaryButton} appearance="primary" onClick={handleDismiss}>
                                Done
                            </Button>
                        )}
                        {state.error && (
                            <>
                                <Button className={styles.secondaryButton} appearance="outline" onClick={handleDismiss}>
                                    Cancel
                                </Button>
                                <Button className={styles.primaryButton} appearance="primary" onClick={handleStart}>
                                    Try Again
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default MigrationModal;
